import React, {createContext, useContext, useMemo} from 'react'
import PropTypes from 'prop-types'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {getSiteEnvVarValue} from '../utils/site-config'

const BrandContext = createContext(null)

const DEFAULT_BRAND = 'garage'

// Parses BRAND_HOST_MAP safely. Accepts a JSON string or an already-parsed object.
const parseBrandConfig = (value) => {
    if (!value) return null
    if (typeof value === 'object') return value
    if (typeof value !== 'string') return null
    try {
        const parsed = JSON.parse(value)
        if (!parsed || typeof parsed !== 'object') return null
        return parsed
    } catch (_) {
        return null
    }
}

// Resolved brand host mapping (runtime config > env > defaults).
const getBrandHostMappingValue = (siteId) => {
    try {
        const config = getConfig()
        const brandHostMap = getSiteEnvVarValue(config.app, 'BRAND_HOST_MAP', siteId)
        if (brandHostMap) return brandHostMap
    } catch (_) {
        // getConfig may not be available in some test/runtime contexts
    }
    return null
}

// Lazy-resolved brand config — getConfig() may not be ready at module import time.
// Keyed by siteId to avoid cross-site cache poisoning in SSR environments.
const _brandConfigCache = {}

export const getBrandConfig = (siteId) => {
    const key = siteId || '__default__'
    if (!_brandConfigCache[key]) {
        _brandConfigCache[key] = parseBrandConfig(getBrandHostMappingValue(siteId))
    }
    return _brandConfigCache[key]
}

// Normalizes host input from a hostname or full URL for matching.
const normalizeHostInput = (input = '') => {
    const trimmed = input.toLowerCase().trim()
    if (!trimmed) {
        return {host: '', hostWithPort: '', port: ''}
    }
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
        try {
            const url = new URL(trimmed)
            return {
                host: url.hostname.toLowerCase(),
                hostWithPort: url.host.toLowerCase(),
                port: url.port
            }
        } catch (_) {
            // fall through to best-effort parsing
        }
    }

    const withoutPath = trimmed.split('/')[0]
    const [hostname, port] = withoutPath.split(':')
    return {
        host: (hostname || '').toLowerCase(),
        hostWithPort: withoutPath.toLowerCase(),
        port: port || ''
    }
}

// Finds the brand ID matching a host from BRAND_CONFIG.
const findBrandConfigMatch = (host = '', siteId) => {
    const config = getBrandConfig(siteId)
    if (!config) return null
    for (const [brandId, url] of Object.entries(config)) {
        try {
            const urlHost = new URL(url).hostname.toLowerCase()
            if (host === urlHost || host.includes(urlHost)) return brandId
        } catch (_) {
            if (host.includes(url.toLowerCase())) return brandId
        }
    }
    return null
}

/**
 * Resolves brand from hostname (subdomain or domain).
 *
 * Local dev:   garage.localhost:3000 / dynamite.localhost:3000
 * Production:  garage.co.uk / dynamite.co.uk (or any domain containing the brand name)
 *
 * Falls back to 'garage' if no brand match is found in hostname.
 *
 * @param {string} hostname - e.g. 'garage.localhost', 'dynamite.localhost', 'garage.co.uk'
 * @returns {string} Brand ID ('garage' or 'dynamite')
 */
export function resolveBrandFromUrl(hostname = '') {
    const {host} = normalizeHostInput(hostname)
    const match = findBrandConfigMatch(host)
    if (match) {
        return match
    }

    // Local dev fallback (garage.localhost / dynamite.localhost)
    if (host.includes('dynamite')) return 'dynamite'
    if (host.includes('garage')) return 'garage'

    return DEFAULT_BRAND
}

// Maps canonical brand IDs ('garage' / 'dynamite') to the short 3-letter
// codes ('grg' / 'dyn') expected by upstream services like VickyAI.
const BRAND_CODES = {
    garage: 'GRG',
    dynamite: 'DYN'
}

export const toBrandCode = (brandId) => BRAND_CODES[brandId] || brandId

/**
 * Builds the destination URL for switching brands, preserving path/query/hash.
 * Handles configured hosts and localhost subdomains.
 */
export const getBrandSwitchUrl = (
    hostname = '',
    targetBrandId,
    {pathname = '', search = '', hash = '', protocol = '', siteId} = {}
) => {
    if (!targetBrandId) return null

    const {host, hostWithPort, port} = normalizeHostInput(hostname)
    const match = findBrandConfigMatch(host, siteId)
    if (match) {
        const baseUrl = getBrandConfig(siteId)?.[targetBrandId]
        if (!baseUrl) return null
        const cleanBase = baseUrl.replace(/\/$/, '')
        const cleanPath = pathname ? (pathname.startsWith('/') ? pathname : `/${pathname}`) : ''
        return `${cleanBase}${cleanPath}${search || ''}${hash || ''}`
    }

    if (host === 'localhost' || host.endsWith('.localhost')) {
        const portSuffix = port ? `:${port}` : ''
        const scheme = protocol || 'http:'
        const baseUrl = `${scheme}//${targetBrandId}.localhost${portSuffix}`
        const cleanPath = pathname ? (pathname.startsWith('/') ? pathname : `/${pathname}`) : ''
        return `${baseUrl}${cleanPath}${search || ''}${hash || ''}`
    }

    if (hostWithPort === 'localhost') {
        const scheme = protocol || 'http:'
        const baseUrl = `${scheme}//${targetBrandId}.localhost`
        const cleanPath = pathname ? (pathname.startsWith('/') ? pathname : `/${pathname}`) : ''
        return `${baseUrl}${cleanPath}${search || ''}${hash || ''}`
    }

    return null
}

/**
 * Provides brand information to the component tree.
 * Brand is resolved from hostname and passed via props from _app-config.
 */
export const BrandProvider = ({children, brandId: propBrandId}) => {
    const brandData = useMemo(() => {
        const brandId = propBrandId || DEFAULT_BRAND

        return {
            brandId,
            isGarage: brandId === 'garage',
            isDynamite: brandId === 'dynamite'
        }
    }, [propBrandId])

    return <BrandContext.Provider value={brandData}>{children}</BrandContext.Provider>
}

BrandProvider.propTypes = {
    children: PropTypes.node.isRequired,
    brandId: PropTypes.string
}

/**
 * Hook to access the current brand context.
 * Returns { brandId, isGarage, isDynamite }
 */
export const useBrand = () => {
    const context = useContext(BrandContext)
    if (!context) {
        throw new Error('useBrand must be used within a BrandProvider')
    }
    return context
}

export default BrandContext
